import React, { useEffect, useState } from 'react';
import { Save, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { logService } from '../../lib/logService';
import type { MobileBuilderFlag } from '../../lib/featureFlags';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SETTING_KEY = 'mobile_builder_enabled';
const DEFAULT_FLAG: MobileBuilderFlag = { platformMode: 'off' };

interface ValidationErrors {
  percentage?: string;
  seed?: string;
  allowlist?: string;
}

export const AdminMobileBuilder: React.FC = () => {
  const [flag, setFlag] = useState<MobileBuilderFlag>(DEFAULT_FLAG);
  const [allowlistRaw, setAllowlistRaw] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<'success' | 'error' | null>(null);
  const [errors, setErrors] = useState<ValidationErrors>({});

  useEffect(() => {
    const fetchFlag = async () => {
      try {
        const { data, error } = await supabase.rpc('get_platform_setting', { p_key: SETTING_KEY });
        if (error) {
          logService.error('adminMobileBuilder.fetch', error.message, { supabase_code: error.code });
        } else if (data) {
          const parsed = data as MobileBuilderFlag;
          setFlag(parsed);
          setAllowlistRaw(parsed.canaryAllowlist?.join(', ') ?? '');
        }
      } catch (err) {
        logService.error(
          'adminMobileBuilder.fetch',
          err instanceof Error ? err.message : 'Unknown error',
          { error: err as Error },
        );
      } finally {
        setLoading(false);
      }
    };
    fetchFlag();
  }, []);

  const validate = (): boolean => {
    const next: ValidationErrors = {};

    if (flag.platformMode === 'percentage') {
      const pct = flag.percentageBucket?.percentage;
      if (pct === undefined || !Number.isInteger(pct) || pct < 0 || pct > 100) {
        next.percentage = 'Inteiro entre 0 e 100';
      }
      if (!flag.percentageBucket?.seed || flag.percentageBucket.seed.trim() === '') {
        next.seed = 'Seed é obrigatório quando percentage';
      }
    }

    if (flag.platformMode === 'canary') {
      const emails = allowlistRaw
        .split(',')
        .map((e) => e.trim())
        .filter((e) => e.length > 0);
      const invalid = emails.filter((e) => !EMAIL_PATTERN.test(e));
      if (invalid.length > 0) {
        next.allowlist = `Emails inválidos: ${invalid.join(', ')}`;
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload: MobileBuilderFlag = { platformMode: flag.platformMode };
    if (flag.platformMode === 'canary') {
      payload.canaryAllowlist = allowlistRaw
        .split(',')
        .map((e) => e.trim())
        .filter((e) => e.length > 0);
    }
    if (flag.platformMode === 'percentage' && flag.percentageBucket) {
      payload.percentageBucket = {
        percentage: flag.percentageBucket.percentage,
        seed: flag.percentageBucket.seed,
      };
    }

    setSaving(true);
    try {
      const { error } = await supabase.rpc('admin_set_platform_setting', {
        p_key: SETTING_KEY,
        p_value: payload as unknown as Record<string, unknown>,
      });
      if (error) {
        logService.error('adminMobileBuilder.save', error.message, { supabase_code: error.code });
        setSaveResult('error');
      } else {
        setFlag(payload);
        setSaveResult('success');
      }
    } catch (err) {
      logService.error(
        'adminMobileBuilder.save',
        err instanceof Error ? err.message : 'Unknown error',
        { error: err as Error },
      );
      setSaveResult('error');
    } finally {
      setSaving(false);
      setTimeout(() => setSaveResult(null), 3000);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-2">Mobile Builder</h1>
      <p className="text-sm text-neutral-500 mb-6">
        Controle qual surface (mobile/desktop) renderiza o editor de formulários. Ative gradualmente via canary ou rollout percentage.
      </p>

      <div className="space-y-6 max-w-2xl">
        <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-5">
          <label className="text-xs text-neutral-400 mb-1 block">Modo de ativação</label>
          <select
            value={flag.platformMode}
            onChange={(e) =>
              setFlag({ ...flag, platformMode: e.target.value as MobileBuilderFlag['platformMode'] })
            }
            className="w-full bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-base sm:text-sm text-white"
          >
            <option value="off">Off — sempre desktop</option>
            <option value="on">On — todos elegíveis (breakpoint decide)</option>
            <option value="canary">Canary — só emails listados</option>
            <option value="percentage">Percentage — bucket determinístico</option>
          </select>
          <p className="text-xs text-neutral-500 mt-2">
            {flag.platformMode === 'off' && 'Builder mobile desativado. Todos usuários veem desktop.'}
            {flag.platformMode === 'on' && 'Builder mobile habilitado pra todos. Breakpoint (max-width: 768px) decide.'}
            {flag.platformMode === 'canary' && 'Só emails na allowlist abaixo entram no canary.'}
            {flag.platformMode === 'percentage' && 'Bucket determinístico baseado em userId + seed; mesmo user sempre cai no mesmo bucket.'}
          </p>
        </div>

        {flag.platformMode === 'canary' && (
          <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-5">
            <label className="text-xs text-neutral-400 mb-1 block">Allowlist de emails (separados por vírgula)</label>
            <textarea
              value={allowlistRaw}
              onChange={(e) => setAllowlistRaw(e.target.value)}
              placeholder="user1@example.com, user2@example.com"
              rows={3}
              className="w-full bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-base sm:text-sm text-white placeholder-neutral-600"
            />
            {errors.allowlist && <p className="text-xs text-red-400 mt-1">{errors.allowlist}</p>}
          </div>
        )}

        {flag.platformMode === 'percentage' && (
          <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-5 space-y-4">
            <div>
              <label className="text-xs text-neutral-400 mb-1 block">Percentage (0-100)</label>
              <input
                type="number"
                min={0}
                max={100}
                step={1}
                value={flag.percentageBucket?.percentage ?? 0}
                onChange={(e) =>
                  setFlag({
                    ...flag,
                    percentageBucket: {
                      percentage: parseInt(e.target.value, 10) || 0,
                      seed: flag.percentageBucket?.seed ?? '',
                    },
                  })
                }
                className="w-full bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-base sm:text-sm text-white"
              />
              {errors.percentage && <p className="text-xs text-red-400 mt-1">{errors.percentage}</p>}
            </div>
            <div>
              <label className="text-xs text-neutral-400 mb-1 block">Seed (string estável)</label>
              <input
                type="text"
                value={flag.percentageBucket?.seed ?? ''}
                onChange={(e) =>
                  setFlag({
                    ...flag,
                    percentageBucket: {
                      percentage: flag.percentageBucket?.percentage ?? 0,
                      seed: e.target.value,
                    },
                  })
                }
                placeholder="ex: mb-rollout-2026-05"
                className="w-full bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-base sm:text-sm text-white placeholder-neutral-600"
              />
              {errors.seed && <p className="text-xs text-red-400 mt-1">{errors.seed}</p>}
              <p className="text-xs text-neutral-500 mt-1">
                Mudar o seed redistribui buckets. Use seeds estáveis para rollout consistente.
              </p>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/15 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Salvando...' : 'Salvar Configurações'}
          </button>
          {saveResult === 'success' && (
            <span className="flex items-center gap-1 text-sm text-green-400">
              <CheckCircle className="w-4 h-4" /> Salvo com sucesso
            </span>
          )}
          {saveResult === 'error' && (
            <span className="flex items-center gap-1 text-sm text-red-400">
              <AlertCircle className="w-4 h-4" /> Erro ao salvar
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
